import { convertToDateDefault } from 'js/library/utils/helpers';

const isEmptyValue = (value) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const mergeEmptyValues = (current, incoming) => {
  if (Array.isArray(current) || Array.isArray(incoming)) return current ?? incoming;
  if (current && typeof current === 'object' && incoming && typeof incoming === 'object') {
    const merged = { ...current };
    Object.entries(incoming).forEach(([key, value]) => {
      merged[key] = key in merged ? mergeEmptyValues(merged[key], value) : value;
    });
    return merged;
  }
  return isEmptyValue(current) ? incoming : current;
};

export function processPreData({ preData = {}, valuesForm = {}, form, accessToken }) {
  const newValues = mergeEmptyValues(valuesForm, preData);

  // copia chaves simples do preData, exceto as listas e otherBlocks
  const skipKeys = [
    'documentList',
    'contactList',
    'addressList',
    'aliasName',
    'name',
    'otherBlocks',
  ];
  Object.keys(preData).forEach((k) => {
    if (!skipKeys.includes(k)) newValues[k] = mergeEmptyValues(newValues[k], preData[k]);
  });

  if (preData.aliasName) newValues.aliasName = preData.aliasName;
  if (preData.name) newValues.name = { value: preData.name };

  // Processa campos root/others com indexKey (ex: synoroinvest)
  const rootFields = (form?.formFields || []).filter(
    (f) => (f.type === 'root' || f.type === 'others') && f.indexKey && f.active !== false
  );

  rootFields.forEach((f) => {
    const fieldName = f.fieldName;
    // Se já não tem valor, usa o accessToken
    if (!newValues[fieldName] && accessToken) {
      newValues[fieldName] = accessToken;
    }
  });

  // Processa campos root/others do tipo date (converte timestamp para DD/MM/YYYY)
  const dateFields = (form?.formFields || []).filter(
    (f) =>
      (f.type === 'root' || f.type === 'others') && f.fieldType === 'date' && f.active !== false
  );

  dateFields.forEach((f) => {
    const fieldName = f.fieldName;
    const preValue = preData[fieldName];

    // Se tem valor no preData (timestamp), converte para DD/MM/YYYY
    if (preValue && typeof preValue === 'number') {
      newValues[fieldName] = convertToDateDefault(new Date(preValue));
    }
    // Se não tem no preData mas tem no valuesForm, mantém o valor existente
    else if (!newValues[fieldName] && valuesForm[fieldName]) {
      newValues[fieldName] = valuesForm[fieldName];
    }
  });

  // Processa documentList
  const docFields = (form?.formFields || []).filter(
    (f) => f.type === 'documentList' && f.active !== false
  );

  const docMap = new Map();
  docFields.forEach((f) => {
    const type = f.fieldName;
    const pre = preData.documentList?.find((d) => d.type === type);
    const existing = (valuesForm.documentList || []).find((d) => d.type === type);
    const value = pre?.value ?? existing?.value ?? (f.indexKey ? accessToken : undefined);
    if (value !== undefined) docMap.set(type, { type, value, ...existing });
  });

  (preData.documentList || []).forEach((d) => {
    if (!docMap.has(d.type)) docMap.set(d.type, { ...d });
  });

  (valuesForm.documentList || []).forEach((d) => {
    if (!docMap.has(d.type)) docMap.set(d.type, { ...d });
  });

  newValues.documentList = Array.from(docMap.values());

  // Processa contactList
  const contactFields = (form?.formFields || []).filter(
    (f) => f.type === 'contactList' && f.active !== false
  );

  const contactMap = new Map();
  contactFields.forEach((f) => {
    const type = f.fieldName;
    const pre = preData.contactList?.find((c) => c.type === type);
    const existing = (valuesForm.contactList || []).find((c) => c.type === type);

    const value = pre?.value ?? existing?.value ?? (f.indexKey ? accessToken : undefined);

    if (value !== undefined) {
      if (type === 'cellPhone' || f.fieldType === 'email') {
        contactMap.set(type, { type, value });
      } else {
        let phoneValue = value;
        if (typeof value === 'string' && value.includes('+')) {
          phoneValue = value.replace(/\D/g, '');
          if (phoneValue.startsWith('55')) {
            phoneValue = phoneValue.substring(2);
          }
        }

        contactMap.set(type, {
          type,
          phone: phoneValue,
          value: value,
          ...(pre?.mask ? { mask: pre.mask } : existing?.mask ? { mask: existing.mask } : {}),
          ...(pre?.isWhatsapp !== undefined
            ? { isWhatsapp: pre.isWhatsapp }
            : existing?.isWhatsapp !== undefined
              ? { isWhatsapp: existing.isWhatsapp }
              : { isWhatsapp: false }),
        });
      }
    }
  });

  (preData.contactList || []).forEach((c) => {
    if (!contactMap.has(c.type)) {
      if (c.type === 'cellPhone' || c.type === 'email') {
        contactMap.set(c.type, { type: c.type, value: c.value });
      } else {
        let phoneValue = c.value;
        if (typeof c.value === 'string' && c.value.includes('+')) {
          phoneValue = c.value.replace(/\D/g, '');
          if (phoneValue.startsWith('55')) {
            phoneValue = phoneValue.substring(2);
          }
        }

        contactMap.set(c.type, {
          type: c.type,
          phone: phoneValue,
          value: c.value,
          ...(c.mask ? { mask: c.mask } : {}),
          isWhatsapp: c.isWhatsapp ?? false,
        });
      }
    }
  });

  (valuesForm.contactList || []).forEach((c) => {
    if (!contactMap.has(c.type)) {
      if (c.type === 'cellPhone' || c.type === 'email') {
        contactMap.set(c.type, { type: c.type, value: c.value });
      } else {
        let phoneValue = c.phone || c.value;
        if (typeof phoneValue === 'string' && phoneValue.includes('+')) {
          phoneValue = phoneValue.replace(/\D/g, '');
          if (phoneValue.startsWith('55')) {
            phoneValue = phoneValue.substring(2);
          }
        }

        contactMap.set(c.type, {
          type: c.type,
          phone: phoneValue,
          value: c.value,
          ...(c.mask ? { mask: c.mask } : {}),
          isWhatsapp: c.isWhatsapp ?? false,
        });
      }
    }
  });

  newValues.contactList = Array.from(contactMap.values());

  // Processa addressList
  const hasTopAddressField = (form?.formFields || []).some(
    (f) => f.type === 'addressList' && f.active !== false
  );
  if (hasTopAddressField) {
    const srcAddr = preData.addressList?.[0] ?? valuesForm.addressList?.[0];
    if (srcAddr) {
      const addressObj = {
        zipCode: srcAddr.zipCode ?? srcAddr.cep ?? srcAddr.zip ?? undefined,
        city: srcAddr.city ?? undefined,
        neighborhood: srcAddr.neighborhood ?? undefined,
        state: srcAddr.state ?? undefined,
        streetAve: srcAddr.streetAve ?? srcAddr.street ?? undefined,
        number: srcAddr.number ?? srcAddr.streetNumber ?? undefined,
        extra: srcAddr.extra ?? srcAddr.complement ?? undefined,
      };
      Object.keys(addressObj).forEach((k) => addressObj[k] === undefined && delete addressObj[k]);
      if (Object.keys(addressObj).length) newValues.addressList = [addressObj];
    }
  } else {
    if (preData.addressList?.[0] && !newValues.addressList) {
      newValues.addressList = [preData.addressList[0]];
    } else if (!newValues.addressList && valuesForm.addressList?.[0]) {
      newValues.addressList = [valuesForm.addressList[0]];
    }
  }

  [
    'cep',
    'zipCode',
    'city',
    'state',
    'streetAve',
    'neighborhood',
    'number',
    'complement',
    'extra',
  ].forEach((k) => {
    if (k in newValues && k !== 'addressList') delete newValues[k];
  });

  // Processa otherBlocks
  if (form?.otherBlocks) {
    Object.entries(form.otherBlocks).forEach(([blockKey, blockConfig]) => {
      const preBlock = preData.otherBlocks?.[blockKey] || {};
      const existingBlock = valuesForm.otherBlocks?.[blockKey] || {};
      newValues.otherBlocks[blockKey] = {
        ...(newValues.otherBlocks[blockKey] || {}),
        ...(existingBlock || {}),
      };

      (blockConfig.fields || []).forEach((field) => {
        const { type, fieldName } = field;

        if (type === 'root') {
          if (preBlock[fieldName] !== undefined) {
            // Se é um campo de data e o valor é timestamp, converte para DD/MM/YYYY
            if (field.fieldType === 'date' && typeof preBlock[fieldName] === 'number') {
              newValues.otherBlocks[blockKey][fieldName] = convertToDateDefault(
                new Date(preBlock[fieldName])
              );
            } else {
              newValues.otherBlocks[blockKey][fieldName] = preBlock[fieldName];
            }
          } else if (existingBlock[fieldName] !== undefined) {
            newValues.otherBlocks[blockKey][fieldName] = existingBlock[fieldName];
          }
        }

        if (type === 'documentList') {
          const preDoc = preBlock.documentList?.find((d) => d.type === fieldName);
          const existingDoc = (existingBlock.documentList || []).find((d) => d.type === fieldName);
          if (preDoc || existingDoc) {
            const list = (newValues.otherBlocks[blockKey].documentList || []).filter(
              (d) => d.type !== fieldName
            );
            list.push(preDoc ?? existingDoc);
            newValues.otherBlocks[blockKey].documentList = list;
          }
        }

        if (type === 'contactList') {
          const preC = preBlock.contactList?.find((c) => c.type === fieldName);
          const existingC = (existingBlock.contactList || []).find((c) => c.type === fieldName);

          if (preC || existingC) {
            const list = (newValues.otherBlocks[blockKey].contactList || []).filter(
              (c) => c.type !== fieldName
            );

            let contactToAdd;
            if (preC) {
              if (fieldName === 'cellPhone' || fieldName === 'email') {
                contactToAdd = { type: fieldName, value: preC.value };
              } else {
                let phoneValue = preC.value;
                if (typeof preC.value === 'string' && preC.value.includes('+')) {
                  phoneValue = preC.value.replace(/\D/g, '');
                  if (phoneValue.startsWith('55')) {
                    phoneValue = phoneValue.substring(2);
                  }
                }
                contactToAdd = {
                  type: fieldName,
                  phone: phoneValue,
                  value: preC.value,
                  ...(preC.mask ? { mask: preC.mask } : {}),
                  isWhatsapp: preC.isWhatsapp ?? false,
                };
              }
            } else {
              contactToAdd = existingC;
            }

            list.push(contactToAdd);
            newValues.otherBlocks[blockKey].contactList = list;
          }
        }

        if (type === 'optInList') {
          const preO = preBlock.optInList?.find(
            (o) => o.type === fieldName || o.optInId?.includes(fieldName)
          );
          const existingO = (existingBlock.optInList || []).find(
            (o) => o.optInId?.includes(fieldName) || o.type === fieldName
          );
          if (preO || existingO) {
            const list = (newValues.otherBlocks[blockKey].optInList || []).filter(
              (o) => !(o.optInId || '').includes(fieldName)
            );
            list.push(preO ?? existingO);
            newValues.otherBlocks[blockKey].optInList = list;
          }
        }

        if (type === 'addressList') {
          const src = preBlock.addressList?.[0] ?? existingBlock.addressList?.[0];
          if (src) {
            newValues.otherBlocks[blockKey].addressList = [src];
          }
        }
      });
    });
  }

  return newValues;
}
